/**
 * Attendance sub-tab — daily P / H / A marking per staff for a
 * picked date. Web mirror of mobile `AttendanceTab.tsx`.
 *
 * Tap a pill to mark; tap the same pill again to clear (toggle
 * semantics). Sticky idempotent doc id `{staffId}_{YYYY-MM-DD}`
 * so the same call updates the same row.
 */
import { useMemo, useState } from 'react';

import {
  clearStaffAttendance,
  dateKey,
  markStaffAttendance,
  useStaff,
  useStaffAttendance,
  type StaffAttendanceStatus,
} from '@/lib/staff';

const STATUS_TINTS: Record<StaffAttendanceStatus, { bg: string; fg: string; label: string }> = {
  present: { bg: '#DCFCE7', fg: '#15803D', label: 'P' },
  half:    { bg: '#FEF3C7', fg: '#B45309', label: 'H' },
  absent:  { bg: '#FEE2E2', fg: '#B91C1C', label: 'A' },
};

function isoToday(): string {
  return dateKey(new Date());
}

export function AttendanceTab({ orgId }: { orgId: string | null }) {
  const [date, setDate] = useState(isoToday());
  const month = useMemo(() => {
    const [y, m] = date.split('-').map((x) => parseInt(x, 10));
    return new Date(y, (m ?? 1) - 1, 1);
  }, [date]);

  const { data: staff, loading: staffLoading } = useStaff(orgId);
  const { byStaff, loading: attLoading } = useStaffAttendance(orgId, month);
  const [busy, setBusy] = useState<string | null>(null);

  const active = useMemo(() => staff.filter((s) => !s.archivedAt), [staff]);

  async function setStatus(staffId: string, status: StaffAttendanceStatus) {
    if (!orgId) return;
    const current = byStaff[staffId]?.find((a) => a.date === date)?.status;
    const key = `${staffId}-${status}`;
    setBusy(key);
    try {
      if (current === status) {
        // Toggle off → clear
        await clearStaffAttendance({ staffId, date });
      } else {
        await markStaffAttendance({ staffId, orgId, date, status });
      }
    } catch (e) {
      console.warn('[AttendanceTab] mark failed:', e);
    } finally {
      setBusy(null);
    }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {/* Date picker */}
      <div
        className="card"
        style={{
          padding: 12,
          background: 'var(--surface)',
          border: '1px solid var(--border)',
          borderRadius: 10,
          display: 'flex',
          alignItems: 'center',
          gap: 12,
        }}
      >
        <i className="ph ph-calendar" style={{ fontSize: 18 }} />
        <input
          className="input"
          type="date"
          value={date}
          onChange={(e) => {
            const v = e.currentTarget.value;
            setDate(v);
          }}
          style={{ flex: 1 }}
        />
        <button
          type="button"
          className="btn-secondary"
          onClick={() => setDate(isoToday())}
        >
          Today
        </button>
      </div>

      {staffLoading || attLoading ? (
        <div style={{ padding: 24, color: 'var(--text-secondary)' }}>Loading…</div>
      ) : active.length === 0 ? (
        <div
          style={{
            padding: 48,
            textAlign: 'center',
            color: 'var(--text-secondary)',
          }}
        >
          <i className="ph ph-users-three" style={{ fontSize: 48, opacity: 0.5 }} />
          <div style={{ marginTop: 12, fontWeight: 600 }}>No active staff</div>
          <div style={{ marginTop: 4, fontSize: 14 }}>
            Add staff in the <strong>Staff</strong> tab to mark attendance.
          </div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {active.map((s) => {
            const current = byStaff[s.id]?.find((a) => a.date === date)?.status;
            return (
              <div
                key={s.id}
                className="card"
                style={{
                  padding: 12,
                  background: 'var(--surface)',
                  border: '1px solid var(--border)',
                  borderRadius: 10,
                  display: 'grid',
                  gridTemplateColumns: '1fr auto',
                  gap: 12,
                  alignItems: 'center',
                }}
              >
                <div>
                  <div style={{ fontWeight: 600, fontSize: 14 }}>{s.name}</div>
                  <div style={{ marginTop: 2, fontSize: 12, color: 'var(--text-secondary)' }}>
                    {s.role}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 6 }}>
                  {(['present', 'half', 'absent'] as StaffAttendanceStatus[]).map((status) => {
                    const tint = STATUS_TINTS[status];
                    const active = current === status;
                    const isBusy = busy === `${s.id}-${status}`;
                    return (
                      <button
                        key={status}
                        type="button"
                        onClick={() => setStatus(s.id, status)}
                        disabled={isBusy}
                        title={status === 'present' ? 'Present' : status === 'half' ? 'Half day' : 'Absent'}
                        style={{
                          width: 38,
                          height: 38,
                          borderRadius: 8,
                          border: active ? `2px solid ${tint.fg}` : '1px solid var(--border)',
                          background: active ? tint.bg : 'var(--surface)',
                          color: active ? tint.fg : 'var(--text-secondary)',
                          fontWeight: 700,
                          fontSize: 14,
                          cursor: isBusy ? 'wait' : 'pointer',
                          opacity: isBusy ? 0.5 : 1,
                        }}
                      >
                        {tint.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <div style={{ fontSize: 11, color: 'var(--text-tertiary)', textAlign: 'center' }}>
        Tap P (present), H (half day), or A (absent). Tap again to clear.
      </div>
    </div>
  );
}
